import { DEFAULT_NETWORK_NAME, type NetworkName } from "../networks";
import { Coin } from "../wallet";
import { baseFetch } from "../utils/fetch";

/**
 * The default base URLs for esplora API providers.
 *
 * Mainnet, mutinynet, and signet point at Ark Labs–operated
 * mempool deployments (mempool.space-compatible esplora API).
 * Testnet falls back to the public mempool.space deployment
 * because Ark doesn't host it. Regtest assumes a local arkade-regtest
 * stack exposing mempool's esplora API on the standard port.
 */
export const ESPLORA_URL: Record<NetworkName, string> = {
    bitcoin: "https://mempool.arkade.sh/api",
    testnet: "https://mempool.space/testnet/api",
    signet: "https://mempool.signet.arkade.sh/api",
    mutinynet: "https://mempool.mutinynet.arkade.sh/api",
    regtest: "http://localhost:3000/api",
};

export type ExplorerTransaction = {
    txid: string;
    /**
     * Inputs as returned by Esplora's `/address/:addr/txs`, each carrying the
     * outpoint it spends (`txid:vout`). Optional: not every provider populates
     * it (the electrum provider omits inputs), so consumers that correlate
     * spenders must tolerate its absence. Used to recover a boarding output's
     * spending (commitment) tx when `/outspends` omits the spender txid.
     */
    vin?: {
        txid: string;
        vout: number;
    }[];
    vout: {
        scriptpubkey_address: string;
        value: string;
    }[];
    status: {
        confirmed: boolean;
        block_time: number;
    };
};

export interface OnchainProvider {
    /**
     * Fetch spendable onchain outputs for an address.
     *
     * @param address - Bitcoin address to query
     * @returns Spendable onchain outputs for the address
     * @see Coin
     */
    getCoins(address: string): Promise<Coin[]>;

    /**
     * Fetch the current fastest fee rate estimate.
     *
     * @returns Fee rate in sats/vB, if available
     * @remarks
     * Implementations may return `undefined` when the backing service does not expose
     * a usable fee estimate.
     */
    getFeeRate(): Promise<number | undefined>;

    /**
     * Broadcast a single transaction or a 1P1C package.
     *
     * @param txs - One or more raw transaction hex strings
     * @returns Broadcast transaction id
     * @throws Error if the broadcast request fails or the package shape is invalid
     */
    broadcastTransaction(...txs: string[]): Promise<string>;

    /**
     * Fetch outspend information for every output in a transaction.
     *
     * @param txid - Transaction id to inspect
     * @returns Per-output spend status information. `txid` (the spender) may be
     *   absent even when `spent` is true: some Esplora deployments
     *   (e.g. mempool.arkade.sh) omit it from `/outspends`.
     * @see getTxStatus
     */
    getTxOutspends(txid: string): Promise<{ spent: boolean; txid?: string }[]>;

    /**
     * Fetch transactions associated with an address.
     *
     * @param address - Bitcoin address to query
     * @returns Transactions involving the address
     * @see ExplorerTransaction
     */
    getTransactions(address: string): Promise<ExplorerTransaction[]>;

    /**
     * Fetch confirmation status for a transaction.
     *
     * @param txid - Transaction id to inspect
     * @returns Confirmation status and block metadata when confirmed
     * @see getTxOutspends
     */
    getTxStatus(
        txid: string,
    ): Promise<{ confirmed: false } | { confirmed: true; blockTime: number; blockHeight: number }>;
    /**
     * Fetch the current chain tip.
     *
     * @returns Current chain height, block time, and block hash
     */
    getChainTip(): Promise<{
        height: number;
        time: number;
        hash: string;
    }>;

    /**
     * Watch a set of addresses and invoke the callback when transactions are observed.
     *
     * @param addresses - Addresses to monitor
     * @param eventCallback - Callback invoked when matching transactions are seen
     * @returns Stop function that cancels the watch
     * @remarks
     * Implementations may use websockets, server-sent events, polling, or a hybrid strategy.
     * @see getTransactions
     */
    watchAddresses(
        addresses: string[],
        eventCallback: (txs: ExplorerTransaction[]) => void,
    ): Promise<() => void>;
}

/**
 * Implementation of the onchain provider interface for esplora REST API.
 *
 * @see https://mempool.space/docs/api/rest
 * @example
 * ```typescript
 * const provider = new EsploraProvider("https://mempool.space/api");
 * const outputs = await provider.getCoins("bcrt1q679zsd45msawvr7782r0twvmukns3drlstjt77");
 * ```
 */
export class EsploraProvider implements OnchainProvider {
    readonly pollingInterval: number;
    readonly forcePolling: boolean;

    constructor(
        private baseUrl: string = ESPLORA_URL[DEFAULT_NETWORK_NAME],
        opts?: {
            /** Polling interval in milliseconds. */
            pollingInterval?: number;

            /** Force polling even when websocket transport is available. */
            forcePolling?: boolean;
        },
    ) {
        this.pollingInterval = opts?.pollingInterval ?? 15_000;
        this.forcePolling = opts?.forcePolling ?? false;
    }

    async getCoins(address: string): Promise<Coin[]> {
        const response = await baseFetch(`${this.baseUrl}/address/${address}/utxo`);
        if (!response.ok) {
            throw new Error(`Failed to fetch UTXOs: ${response.statusText}`);
        }
        return response.json();
    }

    async getFeeRate(): Promise<number | undefined> {
        const response = await baseFetch(`${this.baseUrl}/fee-estimates`);
        // Not every Esplora backend serves /fee-estimates — mempool returns 404
        // on regtest, where it has no fee history. Every caller falls back to
        // MIN_FEE_RATE when this is undefined, so degrade gracefully on a missing
        // endpoint rather than throwing and defeating those fallbacks. Other
        // (e.g. 5xx) failures still surface.
        if (response.status === 404) {
            return undefined;
        }
        if (!response.ok) {
            throw new Error(`Failed to fetch fee rate: ${response.statusText}`);
        }
        const fees = (await response.json()) as Record<string, number>;
        return fees["1"] ?? undefined;
    }

    async broadcastTransaction(...txs: string[]): Promise<string> {
        switch (txs.length) {
            case 1:
                return this.broadcastTx(txs[0]);
            case 2:
                return this.broadcastPackage(txs[0], txs[1]);
            default:
                throw new Error("Only 1 or 1C1P package can be broadcast");
        }
    }

    async getTxOutspends(txid: string): Promise<{ spent: boolean; txid?: string }[]> {
        const response = await baseFetch(`${this.baseUrl}/tx/${txid}/outspends`);
        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Failed to get transaction outspends: ${error}`);
        }

        return response.json();
    }

    async getTransactions(address: string): Promise<ExplorerTransaction[]> {
        const response = await baseFetch(`${this.baseUrl}/address/${address}/txs`);
        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Failed to get transactions: ${error}`);
        }

        return response.json();
    }

    async getTxStatus(txid: string): Promise<
        | {
              confirmed: false;
          }
        | {
              confirmed: true;
              blockTime: number;
              blockHeight: number;
          }
    > {
        // make sure tx exists in mempool or in block
        const txresponse = await baseFetch(`${this.baseUrl}/tx/${txid}`);
        if (!txresponse.ok) {
            throw new Error(txresponse.statusText);
        }

        const tx = await txresponse.json();
        if (!tx.status.confirmed) {
            return { confirmed: false };
        }

        const response = await baseFetch(`${this.baseUrl}/tx/${txid}/status`);
        if (!response.ok) {
            throw new Error(`Failed to get transaction status: ${response.statusText}`);
        }

        const data = await response.json();
        if (!data.confirmed) {
            return { confirmed: false };
        }

        return {
            confirmed: data.confirmed,
            blockTime: data.block_time,
            blockHeight: data.block_height,
        };
    }

    async watchAddresses(
        addresses: string[],
        callback: (txs: ExplorerTransaction[]) => void,
    ): Promise<() => void> {
        let intervalId: ReturnType<typeof setInterval> | null = null;
        const wsUrl = this.baseUrl.replace(/^http(s)?:/, "ws$1:") + "/v1/ws";

        const poll = async () => {
            const getAllTxs = async () => {
                const txArrays = await Promise.all(
                    addresses.map((address) => this.getTransactions(address)),
                );
                return txArrays.flat();
            };

            // initial fetch to get existing transactions
            const initialTxs = await getAllTxs();

            // we use block_time in key to also notify when a transaction is confirmed
            const txKey = (tx: ExplorerTransaction) => `${tx.txid}_${tx.status.block_time}`;

            // create a set of existing transactions to avoid duplicates
            const existingTxs = new Set(initialTxs.map(txKey));

            // polling for new transactions
            intervalId = setInterval(async () => {
                try {
                    // get current transactions
                    // we will compare with initialTxs to find new ones
                    const currentTxs = await getAllTxs();

                    // filter out transactions that are already in initialTxs
                    const newTxs = currentTxs.filter((tx) => !existingTxs.has(txKey(tx)));

                    if (newTxs.length > 0) {
                        // Update the tracking set instead of growing the array
                        newTxs.forEach((tx) => existingTxs.add(txKey(tx)));
                        callback(newTxs);
                    }
                } catch (error) {
                    console.error("Error in polling mechanism:", error);
                }
            }, this.pollingInterval);
        };

        let ws: WebSocket | null = null;

        const stopFunc = () => {
            if (ws) ws.close();
            if (intervalId) clearInterval(intervalId);
        };

        if (this.forcePolling) {
            await poll();
            return stopFunc;
        }

        try {
            ws = new WebSocket(wsUrl);
            ws.addEventListener("open", () => {
                // subscribe to address updates
                const subscribeMsg: SubscribeMessage = {
                    "track-addresses": addresses,
                };
                ws!.send(JSON.stringify(subscribeMsg));
            });

            ws.addEventListener("message", (event: MessageEvent) => {
                try {
                    const newTxs: ExplorerTransaction[] = [];
                    const message: WebSocketMessage = JSON.parse(event.data.toString());
                    if (!message["multi-address-transactions"]) return;
                    const aux = message["multi-address-transactions"];

                    for (const address in aux) {
                        for (const type of ["mempool", "confirmed", "removed"] as const) {
                            if (!aux[address][type]) continue;
                            newTxs.push(...aux[address][type].filter(isExplorerTransaction));
                        }
                    }
                    // callback with new transactions
                    if (newTxs.length > 0) callback(newTxs);
                } catch (error) {
                    console.error("Failed to process WebSocket message:", error);
                }
            });

            ws.addEventListener("error", async () => {
                // if websocket is not available, fallback to polling
                await poll();
            });
        } catch {
            if (intervalId) clearInterval(intervalId);
            // if websocket is not available, fallback to polling
            await poll();
        }

        return stopFunc;
    }

    async getChainTip(): Promise<{
        height: number;
        time: number;
        hash: string;
    }> {
        // Use the standard Esplora `/blocks` route (newest-first array of recent
        // blocks) rather than `/blocks/tip`: the latter is not part of the Esplora
        // spec — electrs happens to serve it as an alias for `/blocks`, but a
        // strict backend like mempool returns an empty array, which surfaced here
        // as "No chain tip found". `/blocks` works across every Esplora backend.
        const tipBlocks = await baseFetch(`${this.baseUrl}/blocks`);
        if (!tipBlocks.ok) {
            throw new Error(`Failed to get chain tip: ${tipBlocks.statusText}`);
        }

        const tip = await tipBlocks.json();
        if (!isValidBlocksTip(tip)) {
            throw new Error(`Invalid chain tip: ${JSON.stringify(tip)}`);
        }

        if (tip.length === 0) {
            throw new Error("No chain tip found");
        }

        const hash = tip[0].id;
        return {
            height: tip[0].height,
            time: tip[0].mediantime,
            hash,
        };
    }

    private async broadcastPackage(parent: string, child: string): Promise<string> {
        const response = await baseFetch(`${this.baseUrl}/txs/package`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify([parent, child]),
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Failed to broadcast package: ${error}`);
        }

        return response.json();
    }

    private async broadcastTx(tx: string): Promise<string> {
        const response = await baseFetch(`${this.baseUrl}/tx`, {
            method: "POST",
            headers: {
                "Content-Type": "text/plain",
            },
            body: tx,
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(`Failed to broadcast transaction: ${error}`);
        }

        return response.text();
    }
}

function isValidBlocksTip(tip: any): tip is { id: string; height: number; mediantime: number }[] {
    return (
        Array.isArray(tip) &&
        tip.every((t) => {
            return (
                t &&
                typeof t === "object" &&
                typeof t.id === "string" &&
                t.id.length > 0 &&
                typeof t.height === "number" &&
                t.height >= 0 &&
                typeof t.mediantime === "number" &&
                t.mediantime > 0
            );
        })
    );
}

const isExplorerTransaction = (tx: any): tx is ExplorerTransaction => {
    return (
        typeof tx.txid === "string" &&
        (tx.vin === undefined ||
            (Array.isArray(tx.vin) &&
                tx.vin.every(
                    (vin: any) => typeof vin.txid === "string" && typeof vin.vout === "number",
                ))) &&
        Array.isArray(tx.vout) &&
        tx.vout.every(
            (vout: any) =>
                typeof vout.scriptpubkey_address === "string" && typeof vout.value === "number",
        ) &&
        typeof tx.status === "object" &&
        typeof tx.status.confirmed === "boolean"
    );
};

interface SubscribeMessage {
    "track-addresses": string[];
}

interface WebSocketMessage {
    "multi-address-transactions"?: Record<
        string,
        {
            mempool: ExplorerTransaction[];
            confirmed: ExplorerTransaction[];
            removed: ExplorerTransaction[];
        }
    >;
}
